/**
 * Shared field type display helpers.
 *
 * Used by PropertyTypeSlideout, InlinePropertyEditor, and any UI
 * that needs to show a human-readable list of field types.
 */

import type { FieldType } from '@/lib/crm/core/SchemaClient';
import { getPropertyTypeIconComponent } from '@/lib/crm/utils/icons/PropertyTypeIcon';

/** Get the display label for a field type name, using the API-provided labels. */
export function getFieldTypeLabel(
  fieldTypeName: string,
  fieldTypes: FieldType[]
): string {
  const ft = fieldTypes.find(f => f.field_type_name === fieldTypeName);
  return ft?.label ?? fieldTypeName;
}

/** Build select items from field types, filtering out internal-only types. */
export function getFieldTypeItems(fieldTypes: FieldType[]) {
  return fieldTypes
    .filter(ft => ft.type_use_access !== 'internal')
    .map(ft => ({
      id: ft.field_type_name,
      label: ft.label,
      icon: getPropertyTypeIconComponent({
        field_type_name: ft.field_type_name,
      }),
    }));
}

/**
 * Generate a property name from a label.
 * Prefixes with "org_", converts to lowercase snake_case.
 */
export function generatePropertyName(label: string): string {
  const baseName = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

  if (!baseName) {
    return '';
  }
  return `org_${baseName}`;
}

/**
 * Resolve name collisions by appending a numeric suffix.
 */
export function resolveNameCollision(
  baseName: string,
  existingNames: Set<string>,
  excludeName?: string
): string {
  let name = baseName;
  let suffix = 2;

  const namesToCheck = excludeName
    ? new Set([...existingNames].filter(n => n !== excludeName))
    : existingNames;

  while (namesToCheck.has(name)) {
    name = `${baseName}_${suffix}`;
    suffix++;
  }

  return name;
}
